import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@core/environments/environment';
import { LoadingService } from './loading.service';
import { finalize, Observable } from 'rxjs';
import { Pagination } from '@core/models/pagination.model';
import Decimal from 'decimal.js';
import { MFDOrderListResponse } from '@core/models/admin/orders/get-all-cash-orders.model';

@Injectable({
  providedIn: 'root',
})
export class MutualFundService {
  http = inject(HttpClient);
  BASE_URL = `${environment.ADMIN_BASE_URL}/v1/mutual-funds`;
  AUTH_URL = `${environment.ADMIN_BASE_URL}/auth`;

  constructor() {}

  private loadingService = inject(LoadingService);

  getMutualFundsList(
    search_text: string = '',
    start_date?: Date,
    end_date?: Date,
    data: Pagination = new Pagination
  ): Observable<MutualFundsListResponse> {
    const uniqueStateUrl = 'get-mutual-funds-list';
    this.loadingService.setLoading(uniqueStateUrl, true);

    let params = new HttpParams({
      fromObject: {
        take: data.size.toString(),
        skip: data.skip.toString(),
        ...(start_date
          ? { start_date: start_date.toISOString().split('T')[0] }
          : {}),
        ...(end_date ? { end_date: end_date.toISOString().split('T')[0] } : {}),
        ...(search_text
          ? /^\d+$/.test(search_text)
            ? { account_number: search_text }
            : { name: search_text }
          : {}),
      },
    });

    return this.http
      .get<MutualFundsListResponse>(
        `${this.BASE_URL}/list?${params.toString()}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getMutualFundsTransactionsList(
    data: Pagination = new Pagination(),
    search_text: string = '',
    start_date?: Date,
    end_date?: Date
  ): Observable<MutualFundsListResponse> {
    const uniqueStateUrl = 'get-mutual-funds-transactions-list';
    this.loadingService.setLoading(uniqueStateUrl, true);

    let params = new HttpParams({
      fromObject: {
        take: data.size.toString(),
        skip: data.skip.toString(),
        ...(start_date
          ? { start_date: start_date.toISOString().split('T')[0] }
          : {}),
        ...(end_date ? { end_date: end_date.toISOString().split('T')[0] } : {}),
        ...(search_text
          ? /^\d+$/.test(search_text)
            ? { account_number: search_text }
            : { name: search_text }
          : {}),
      },
    });

    return this.http
      .get<MutualFundsListResponse>(
        `${this.BASE_URL}/get-funds-transaction?${params.toString()}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getDividendList(
    data: Pagination = new Pagination(),
    start_date?: Date,
    end_date?: Date
  ): Observable<MutualFundsListResponse> {
    const uniqueStateUrl = 'get-dividend-list';
    this.loadingService.setLoading(uniqueStateUrl, true);

    let params = new HttpParams({
      fromObject: {
        take: data.size.toString(),
        skip: data.skip.toString(),
        ...(start_date
          ? { start_date: start_date.toISOString().split('T')[0] }
          : {}),
        ...(end_date ? { end_date: end_date.toISOString().split('T')[0] } : {}),
      },
    });
    return this.http
      .get<MutualFundsListResponse>(
        `${this.BASE_URL}/get-dividends?${params.toString()}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getIndividualMutualFundsTransactionsList(
    data: Pagination = new Pagination(),
    accountNumber: string,
    start_date?: string,
    end_date?: string
  ): Observable<MutualFundsListResponse> {
    const uniqueStateUrl = 'get-mutual-funds-transactions-list';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const params = new HttpParams({
      fromObject: {
        account_number: accountNumber,
        take: data.size.toString(),
        skip: data.skip.toString(),
        ...(start_date ? { start_date: start_date } : {}),
        ...(end_date ? { end_date: end_date } : {}),
      },
    });

    return this.http
      .get<MutualFundsListResponse>(`${this.BASE_URL}/get-funds-transaction`, {
        params,
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getDividendHistoryListByAccount(
    accountNumber: string
  ): Observable<DividendListResponse> {
    const uniqueStateUrl = 'get-dividend-list';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .get<DividendListResponse>(
        `${this.BASE_URL}/get-dividend-history-by-account`,
        {
          params: new HttpParams().set('account_number', accountNumber),
        }
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  getDividendByAccountNumber(
    accountNumber: string
  ): Observable<DividendByAccountResponse> {
    const uniqueStateUrl = 'get-dividend-by-account';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<DividendByAccountResponse>(
        `${this.BASE_URL}/get-dividends-by-account`,
        {
          params: new HttpParams().set('account_number', accountNumber),
        }
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getTotalWeight(date: string): Observable<GlobalWeightResponse> {
    const uniqueStateUrl = 'get-global-weight';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<GlobalWeightResponse>(`${this.BASE_URL}/get-global-weight`, {
        params: new HttpParams().set('date', date),
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  getDividendDistribution(
    date: string
  ): Observable<DividendDistributionResponse> {
    const uniqueStateUrl = 'get-dividend-distribution';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<DividendDistributionResponse>(
        `${this.BASE_URL}/get-dividend-distributions`,
        {
          params: new HttpParams().set('date', date),
        }
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getDailyDividend(date: string): Observable<DailyDividendResponse> {
    const uniqueStateUrl = 'get-daily-dividend';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<DailyDividendResponse>(
        `${this.BASE_URL}/get-dividend-distribution-sum-by-date`,
        {
          params: new HttpParams().set('date', date),
        }
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  getTotalPrincipal(date: string): Observable<TotalPrincipalResponse> {
    const uniqueStateUrl = 'get-global-principal';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<TotalPrincipalResponse>(`${this.BASE_URL}/get-global-principal`, {
        params: new HttpParams().set('date', date),
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  getTotalAccured(
    account_number: string,
    date: string
  ): Observable<TotalAccuredResponse> {
    const uniqueStateUrl = 'get-total-accured';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .get<TotalAccuredResponse>(`${this.BASE_URL}/get-accrual-by-account`, {
        params: new HttpParams()
          .set('account_number', account_number)
          .set('date', date),
      })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  revaluateWeight(): Observable<RevaluateWeightResponse> {
    const uniqueStateUrl = 'revaluate-weight';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<RevaluateWeightResponse>(`${this.BASE_URL}/update-weights`, {})
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  revaluateDivident(payload: any): Observable<RevaluateWeightResponse> {
    const uniqueStateUrl = 'revaluate-weight';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<RevaluateWeightResponse>(
        `${this.BASE_URL}/evaluate-dividends`,
        payload
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  

  addRedemption(data: IAddTransactionPayload): Observable<any> {
    const uniqueStateUrl = 'add-transaction';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<any>(`${this.BASE_URL}/add-redemption`, data)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  addSubscription(data: IAddTransactionPayload): Observable<any> {
    const uniqueStateUrl = 'add-transaction';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<any>(`${this.BASE_URL}/add-subscription`, data)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  deleteTransaction(transactionId: string): Observable<ApproveRejectResponse> {
    const uniqueStateUrl = 'delete-transaction';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post<ApproveRejectResponse>(`${this.BASE_URL}/delete-sub-rem?id=${transactionId}`, null)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  retryDeluxeSeeding(transactionId: string): Observable<RevaluateWeightResponse> {
    const uniqueStateUrl = 'retry-deluxe-seeding';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post<RevaluateWeightResponse>(`${this.BASE_URL}/retry_deluxe_transaction_seed`, {transaction_id: transactionId})
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  

  investmentAction(data: IInvestementActionPayload): Observable<any> {
    const uniqueStateUrl = 'add-transaction';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http.post<any>(`${this.BASE_URL}/investment-action`, data).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }
  addDividend(data: IDividendPayload): Observable<any> {
    return this.http.post<any>(`${this.BASE_URL}/create-dividend`, data);
  }
  editDividend(data: IDividendPayload): Observable<any> {
    return this.http.post<any>(`${this.BASE_URL}/update-dividend`, data);
  }
  approveTransaction(transactionId: string, value_date?: Date): Observable<ApproveRejectResponse> {
    return this.processTransactionStatusChange(transactionId, 'approved', 'approve-transaction',  value_date);
  }

  rejectTransaction(transactionId: string): Observable<ApproveRejectResponse> {
    return this.processTransactionStatusChange(transactionId, 'denied', 'reject-transaction');
  }

  private processTransactionStatusChange(
    transactionId: string,
    status: 'denied' | 'approved',
    loadingKey: string,
    value_date?: Date
  ): Observable<ApproveRejectResponse> {
    this.loadingService.setLoading(loadingKey, true);

    let payload: { id: string; status: 'denied' | 'approved'; value_date?: Date } = {
      id: transactionId,
      status: status
    };
    if (value_date) {
      payload.value_date = value_date;
    }
    

    return this.http
      .post<ApproveRejectResponse>(`${this.BASE_URL}/approve-or-reject-remdemption`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(loadingKey, false))
      );
  }

    updateDistribution(data: {start_date: string, end_date: string}): Observable<UpdateDistributionResponse> {
    const uniqueStateUrl = 'update distribution';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post<UpdateDistributionResponse>(`${this.BASE_URL}/update-client-distribution-by-date?start_date=${data.start_date}&end_date=${data.end_date}`, 
        {})
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

    getAllMFDTRXRequests(
    data: Pagination = new Pagination(),
    searchText?: string
  ): Observable<MFDOrderListResponse> {
    const uniqueStateUrl = 'get-all-mfd-trx-request';
    this.loadingService.setLoading(uniqueStateUrl, true);

    let query = '';
    if (searchText) {
      query += `${searchText}`;
    } else {
      query += `take=${data.size}&skip=${data.skip}`;
    }
    return this.http
      .get<MFDOrderListResponse>(
        `${this.BASE_URL}/get-all-mfd-trx-request?${query}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  approveOrRejectOrder(
      id: string,
      status: string
    ): Observable<ApproveRejectResponse> {
      const uniqueStateUrl = 'approve-or-reject-mfd-trx';
      this.loadingService.setLoading(uniqueStateUrl, true);
  
      return this.http
        .post<ApproveRejectResponse>(`${this.BASE_URL}/approve-or-reject-mfd-trx`, {
          id,
          status,
        })
        .pipe(
          finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
        );
    }

  seedClientsId(): Observable<string> {
      const uniqueStateUrl = 'seed clients id';
      this.loadingService.setLoading(uniqueStateUrl, true);

      // split the url to get the environment
      const env = this.AUTH_URL.split('/').includes('prod') ? 'prod' : 'dev';
      return this.http
        .post<string>(`${this.AUTH_URL}/seed-deluxe-client-id`, { env: env })
        .pipe(
          finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
        );
  }
}
interface ApproveRejectResponse {
  status: string;
  message: string;
  data?: string | null;
}
interface MutualFundsListResponse {
  status: string;
  message: string;
  data: MutualFundsListData;
}

interface MutualFundsListData {
  count: number;
  data: MutualFundsListData[];
}

interface MutualFundsListData {
  name: string;
  account_number: string;
  amount: number;
  type: string;
  weight: number;
  created_at: string;
  updated_at: string;
  
  dayDate: string;
}

interface DividendListResponse {
  status: string;
  message: string;
  data: DividendListData[];
}

interface DividendListData {
  date: string;
  dividend: number;
}

interface DividendByAccountResponse {
  status: string;
  message: string;
  data: DividendByAccountData;
}

interface DividendByAccountData {
  principal_till_date: number;
  total_dividend: number;
  total_value: number;
  account_number: number;
}
interface SumData {
  dividend: number;
}
interface RevaluateWeightResponse {
  status: string;
  message: string;
}

interface IAddTransactionPayload {
  name: string;
  account_number: string;
  amount: Decimal;
  type: string;
}
export interface IInvestementActionPayload {
  name: string;
  account_number: string;
  amount: string;
  action_type: string;
  date: string;
  
}

interface TotalAccuredResponse {
  status: string;
  message: string;
  data: TotalAccuredData;
}
interface TotalAccuredData {
  _sum: {
    dividend: number;
  };
}
interface TotalPrincipalResponse {
  status: string;
  message: string;
  data: TotalPrincipalData;
}

interface TotalPrincipalData {
  amount: number;
}

interface GlobalWeightResponse {
  status: string;
  message: string;
  data: number;
}

interface IDividendPayload {
  amount: number;
  date: string;
}

interface DailyDividendResponse {
  status: string;
  message: string;
  data: {
    _sum: Dividend;
  };
}
interface Dividend {
  dividend: number;
}

interface DividendDistributionResponse {
  status: string;
  message: string;
  data: DailyDividendData;
}

interface DailyDividendData {
  dividend_amount: DividendAmount;
  distributed_dividend: DistributedDividend[];
}

interface DividendAmount {
  _sum: { dividend: number };
}

interface DistributedDividend {
  dividend: number;
  account_number: string;
  date: string;
  weight: number;
}
interface ApproveRejectPayload {
  id: string;
  status: 'approved' | 'denied';
  date: string;
  admin_user_id:string
}
 

interface ApproveRejectResponse {
  status: string;
  message: string;
  data?: string | null;
}


interface UpdateDistributionResponse {
  status: string;
  message: string;
}